/**
 * Plain-text rules for everything a widget visitor or an agent types (D426, ADR-0067).
 *
 * Text is PLAIN TEXT, end to end: the server never interprets it as HTML or markup and the widget renders it as a text
 * node. The only structure kept is the line break. So a message is normalised (CRLF/CR → LF, surrounding whitespace
 * trimmed), measured in Unicode code points (the same unit PostgreSQL's `char_length` counts, so the kernel and the
 * table CHECK agree), and refused when it carries a control character other than LF (C0, DEL, C1) or a lone UTF-16
 * surrogate (not valid Unicode — the database would reject it later with a 500 instead of a reason code).
 */
import { err, ok } from '@vello/core';
import { refuse } from './reasons.js';
/** The longest message a visitor or an agent may send, in code points. */
export const WIDGET_MESSAGE_MAX_CHARS = 2000;
/** A C0 control except LF (U+000A), DEL, or a C1 control. */
function hasForbiddenControl(text) {
    for (let i = 0; i < text.length; i++) {
        const c = text.charCodeAt(i);
        if ((c <= 0x1f && c !== 0x0a) || (c >= 0x7f && c <= 0x9f))
            return true;
    }
    return false;
}
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;
/** Number of Unicode code points (a surrogate pair counts once). */
export function codePointLength(text) {
    let n = 0;
    for (const _ of text)
        n += 1;
    return n;
}
/** CRLF and lone CR become LF; surrounding whitespace is trimmed. */
export function normalizePlainText(raw) {
    return raw.replace(/\r\n?/g, '\n').trim();
}
/**
 * Validate one plain-text field. Returns the normalised text, or the refusal the caller maps to its own code
 * (`WIDGET_MESSAGE_*` for messages; configuration fields map any refusal to their own field code).
 */
export function validatePlainText(raw, rule) {
    if (typeof raw !== 'string')
        return err(refuse('WIDGET_MESSAGE_EMPTY', 'text is required'));
    if (LONE_SURROGATE.test(raw))
        return err(refuse('WIDGET_MESSAGE_INVALID_CHARS', 'text is not valid unicode'));
    const text = normalizePlainText(raw);
    if (text === '')
        return err(refuse('WIDGET_MESSAGE_EMPTY', 'text is empty'));
    if (hasForbiddenControl(text) || (!rule.allowNewline && text.includes('\n'))) {
        return err(refuse('WIDGET_MESSAGE_INVALID_CHARS', 'text carries a control character'));
    }
    if (codePointLength(text) > rule.maxChars)
        return err(refuse('WIDGET_MESSAGE_TOO_LONG', `text exceeds ${rule.maxChars} characters`));
    return ok(text);
}
/** A widget or agent message: 1..2000 code points of plain text, line breaks allowed. */
export function validateMessageText(raw) {
    return validatePlainText(raw, { maxChars: WIDGET_MESSAGE_MAX_CHARS, allowNewline: true });
}
//# sourceMappingURL=text.js.map